import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const STATUS_BADGE = {
  Active: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  Idle: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
};

const Shimmer = ({ width = '100%', height = 14, style = {} }) => (
  <div
    style={{
      width,
      height,
      borderRadius: 6,
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite',
      ...style,
    }}
  />
);

const SkeletonRow = () => (
  <tr>
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <td key={i} style={{ padding: '14px 12px' }}>
        <Shimmer width={i === 4 ? '90%' : '70%'} />
      </td>
    ))}
  </tr>
);

const Stars = ({ value = 0, size = 15 }) => {
  const safe = Math.max(0, Math.min(5, Number(value || 0)));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, safe - i));
        return (
          <svg
            key={i}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={fill >= 1 ? '#f59e0b' : 'none'}
            stroke={fill > 0 ? '#f59e0b' : '#d1d5db'}
            strokeWidth="2"
          >
            <path d="m11.049 2.927 2.01 6.186h6.505l-5.263 3.822 2.01 6.186-5.262-3.822-5.263 3.822 2.01-6.186L2.535 9.113h6.505l2.009-6.186Z" />
            {fill > 0 && fill < 1 && (
              <defs>
                <clipPath id={`clip-${i}-${size}`}>
                  <rect x="0" y="0" width={24 * fill} height="24" />
                </clipPath>
              </defs>
            )}
            {fill > 0 && fill < 1 && (
              <path
                d="m11.049 2.927 2.01 6.186h6.505l-5.263 3.822 2.01 6.186-5.262-3.822-5.263 3.822 2.01-6.186L2.535 9.113h6.505l2.009-6.186Z"
                fill="#f59e0b"
                clipPath={`url(#clip-${i}-${size})`}
              />
            )}
          </svg>
        );
      })}
      <span style={{ fontSize: 12, color: '#475569', fontWeight: 700, marginLeft: 4 }}>
        {safe.toFixed(1)}
      </span>
    </span>
  );
};

const formatWorkerAddress = (address) => {
  if (!address) return '-';
  const parts = [address.line1, address.line2, address.city, address.state, address.pincode]
    .filter(Boolean)
    .map((value) => String(value).trim())
    .filter(Boolean);
  return parts.length ? parts.join(', ') : '-';
};

function StaffOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const fetchStaff = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/staff-overview');
      const normalizedRows = (res.data?.data || []).map((row) => {
        const normalizedRating = Number(
          row.rating ??
          row.worker_rating ??
          row.performance_rating ??
          row.auto_rating ??
          row.stars ??
          0
        );
        return {
          ...row,
          rating: Number.isFinite(normalizedRating) ? normalizedRating : 0,
        };
      });
      setRows(normalizedRows);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load staff overview');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleViewWorker = async (workerId) => {
    setDetailsLoading(true);
    setDetailsError('');
    setSelectedWorker(null);
    try {
      const res = await api.get(`/admin/staff-overview/${workerId}`);
      setSelectedWorker(res.data?.data || null);
    } catch (err) {
      setDetailsError(err.response?.data?.message || 'Failed to load worker details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeWorkerModal = () => {
    setSelectedWorker(null);
    setDetailsError('');
    setDetailsLoading(false);
  };

  const departments = useMemo(() => {
    const set = new Set(rows.map((r) => r.department).filter(Boolean));
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const nameMatch =
        !search ||
        [
          r.name,
          r.employee_code,
          r.phone,
          r.department,
          r.designation,
          r.current_stage,
          r.branch_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search.toLowerCase()));
      const deptMatch = !deptFilter || r.department === deptFilter;
      const statusMatch = !statusFilter || r.status === statusFilter;
      return nameMatch && deptMatch && statusMatch;
    });
  }, [rows, search, deptFilter, statusFilter]);

  const thStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '12px 12px',
    borderBottom: '1px solid #e2e8f0',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  };

  const tdStyle = { padding: '12px 12px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>

      {/* Header */}
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          padding: '20px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
          Staff Overview
        </h2>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
          All active workers with rating, contact, wages, current work, and full worker detail access.
        </p>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          placeholder="Search by name, code, phone, stage..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '1 1 200px',
            minWidth: 180,
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            fontSize: 13,
            background: '#fff',
            color: '#0f172a',
          }}
        />
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            fontSize: 13,
            background: '#fff',
            color: '#0f172a',
            minWidth: 160,
          }}
        >
          <option value="">All Departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            fontSize: 13,
            background: '#fff',
            color: '#0f172a',
            minWidth: 130,
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Idle">Idle</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 12,
            border: '1px solid #fecaca',
            background: '#fef2f2',
            color: '#b91c1c',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}

      {/* Table */}
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', minWidth: 1180, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Dept</th>
                <th style={thStyle}>Branch</th>
                <th style={thStyle}>Designation</th>
                <th style={thStyle}>Daily Wage</th>
                <th style={thStyle}>Current Work</th>
                <th style={thStyle}>Rating</th>
                <th style={thStyle}>Worker Status</th>
                <th style={thStyle}>Last Active</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}

              {!loading &&
                filtered.map((row) => {
                  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.Idle;
                  return (
                    <tr key={row.db_id || row.id}>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#64748b' }}>
                        {row.employee_code || '-'}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>
                        <div>{row.name || '-'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>
                          {row.phone || '-'} · {row.branch_name || '-'} · {Number(row.rating || 0).toFixed(1)} rating
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{row.phone || '-'}</td>
                      <td style={tdStyle}>{row.department || '-'}</td>
                      <td style={tdStyle}>{row.branch_name || '-'}</td>
                      <td style={tdStyle}>{row.designation || '-'}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>
                        Rs {Number(row.daily_wage || 0).toLocaleString('en-IN')}/day
                      </td>
                      <td style={tdStyle}>
                        {row.current_stage ? (
                          <span>
                            <span
                              style={{
                                display: 'inline-flex',
                                padding: '3px 8px',
                                borderRadius: 6,
                                background: badge.bg,
                                color: badge.color,
                                border: `1px solid ${badge.border}`,
                                fontSize: 11,
                                fontWeight: 700,
                                marginRight: 6,
                              }}
                            >
                              {row.current_stage}
                            </span>
                            <span style={{ fontSize: 11, color: '#64748b' }}>
                              {row.job_card_no || `JC-${row.job_card_id}`}
                            </span>
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              padding: '3px 8px',
                              borderRadius: 6,
                              background: '#f1f5f9',
                              color: '#94a3b8',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            Idle
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <Stars value={row.rating} />
                      </td>
                      <td style={tdStyle}>{row.employment_status || '-'}</td>
                      <td style={tdStyle}>
                        {row.last_active_date
                          ? new Date(row.last_active_date).toLocaleDateString('en-IN')
                          : '-'}
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => handleViewWorker(row.db_id || row.id)}
                          style={{
                            border: '1px solid #cbd5e1',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            borderRadius: 8,
                            padding: '7px 12px',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={12}
                    style={{
                      padding: '40px 12px',
                      textAlign: 'center',
                      color: '#94a3b8',
                      fontSize: 14,
                    }}
                  >
                    {rows.length === 0
                      ? 'No active workers found.'
                      : 'No workers match your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(detailsLoading || detailsError || selectedWorker) && (
        <div
          onClick={closeWorkerModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(1040px, 100%)',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: 18,
              border: '1px solid #e2e8f0',
              boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                  {selectedWorker?.worker?.name || 'Worker Details'}
                </h3>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                  Full worker information for admin review.
                </p>
              </div>
              <button
                type="button"
                onClick={closeWorkerModal}
                style={{
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  borderRadius: 999,
                  width: 34,
                  height: 34,
                  cursor: 'pointer',
                  fontSize: 16,
                  color: '#475569',
                }}
              >
                ×
              </button>
            </div>

            {detailsLoading && (
              <div style={{ display: 'grid', gap: 12 }}>
                <Shimmer height={82} />
                <Shimmer height={140} />
                <Shimmer height={140} />
              </div>
            )}

            {!detailsLoading && detailsError && (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  color: '#b91c1c',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {detailsError}
              </div>
            )}

            {!detailsLoading && selectedWorker?.worker && (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                    gap: 12,
                  }}
                >
                  {[
                    ['Worker Code', selectedWorker.worker.employee_code],
                    ['Phone', selectedWorker.worker.phone],
                    ['Department', selectedWorker.worker.department],
                    ['Branch', selectedWorker.worker.branch_name],
                    ['Role / Type', selectedWorker.worker.designation],
                    ['Daily Wage', `Rs ${Number(selectedWorker.worker.daily_wage || 0).toLocaleString('en-IN')}/day`],
                    ['Employment Status', selectedWorker.worker.employment_status],
                    ['Current Stage', selectedWorker.worker.current_stage || 'No active stage'],
                    ['Job Card', selectedWorker.worker.job_card_no || '-'],
                    ['Last Active', selectedWorker.worker.last_active_date ? new Date(selectedWorker.worker.last_active_date).toLocaleDateString('en-IN') : '-'],
                    ['Joined', selectedWorker.worker.joined_at ? new Date(selectedWorker.worker.joined_at).toLocaleDateString('en-IN') : '-'],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        background: '#f8fafc',
                        padding: 14,
                      }}
                    >
                      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 700 }}>{value || '-'}</div>
                      {label === 'Daily Wage' && (
                        <div style={{ marginTop: 8 }}>
                          <Stars value={selectedWorker.worker.rating} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: '#fff' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Bank Details</h4>
                    {[
                      ['Account Holder', selectedWorker.bank_details?.account_holder],
                      ['Bank Name', selectedWorker.bank_details?.bank_name],
                      ['Account Number', selectedWorker.bank_details?.account_number],
                      ['IFSC', selectedWorker.bank_details?.ifsc_code],
                      ['Branch', selectedWorker.bank_details?.branch_name],
                      ['Account Type', selectedWorker.bank_details?.account_type],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>
                        <span style={{ fontSize: 12, color: '#0f172a', fontWeight: 600, textAlign: 'right' }}>{value || '-'}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: '#fff' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Address</h4>
                    <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600, lineHeight: 1.7 }}>
                      {formatWorkerAddress(selectedWorker.worker.address)}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                      {formatWorkerAddress(selectedWorker.worker.address) === '-'
                        ? 'No worker address is stored in the current worker record.'
                        : 'Worker address captured in the backend record.'}
                    </div>
                  </div>

                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: '#fff' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Skills</h4>
                    {selectedWorker.skills?.length ? selectedWorker.skills.map((skill) => (
                      <div key={skill.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 700 }}>
                          {skill.skill_name}
                          {skill.is_primary ? ' · Primary' : ''}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          Experience: {Number(skill.years_experience || 0).toLocaleString('en-IN')} year(s)
                        </div>
                      </div>
                    )) : <div style={{ fontSize: 13, color: '#94a3b8' }}>No skill records available.</div>}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: '#fff' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Performance History</h4>
                    {selectedWorker.performance_history?.length ? selectedWorker.performance_history.map((item) => (
                      <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                          <Stars value={item.rating || item.auto_rating || item.stars} />
                          <span style={{ fontSize: 11, color: '#64748b' }}>
                            {item.evaluated_at ? new Date(item.evaluated_at).toLocaleDateString('en-IN') : '-'}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                          Auto: {Number(item.auto_rating || 0).toLocaleString('en-IN')} · Stars: {Number(item.stars || 0).toLocaleString('en-IN')}
                        </div>
                        <div style={{ fontSize: 12, color: '#0f172a', marginTop: 6 }}>
                          {item.remarks || 'No remarks'}
                        </div>
                      </div>
                    )) : <div style={{ fontSize: 13, color: '#94a3b8' }}>No performance history available.</div>}
                  </div>

                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: '#fff' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Documents</h4>
                    {selectedWorker.documents?.length ? selectedWorker.documents.map((doc) => (
                      <div key={doc.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 700 }}>{doc.doc_name || doc.doc_type || 'Document'}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Type: {doc.doc_type || '-'} · Number: {doc.doc_number || '-'}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Name on document: {doc.name_on_document || '-'}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Uploaded: {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN') : '-'}
                        </div>
                      </div>
                    )) : (
                      <>
                        <div style={{ fontSize: 13, color: '#94a3b8' }}>No documents available.</div>
                        {selectedWorker.documents_notice && (
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, lineHeight: 1.6 }}>
                            {selectedWorker.documents_notice}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffOverview;
